import { useEffect, useMemo, useRef, useState } from 'react';
import { gsap } from '../lib/gsapContext';
import LibraryCard from '../components/LibraryCard';
import LibraryFilters, { type CategoryFilter } from '../components/LibraryFilters';
import DownloadFormModal from '../components/DownloadFormModal';
import { fetchLibrary, type LibraryItem } from '../lib/api';
import Monolith from '../components/Monolith';
import MagneticButton from '../components/MagneticButton';
import { useDocumentMeta } from '../lib/useDocumentMeta';

export default function Library() {
  useDocumentMeta({
    title: 'Library — Samia Systems',
    description:
      'Sample Technical Blueprints, n8n blueprints, e-books, and prompts. Read the document you would receive from your own Diagnostic.',
  });
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [sector, setSector] = useState<string | null>(null);
  const [selected, setSelected] = useState<LibraryItem | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchLibrary()
      .then((data) => {
        setItems(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Could not load library');
        setLoading(false);
      });
  }, []);

  const availableSectors = useMemo(
    () =>
      Array.from(
        new Set(items.map((i) => i.sector).filter((s): s is string => !!s))
      ).sort(),
    [items]
  );

  const filtered = useMemo(
    () =>
      items.filter((i) => {
        if (category !== 'all' && i.category !== category) return false;
        if (sector && i.sector !== sector) return false;
        return true;
      }),
    [items, category, sector]
  );

  // Hero entrance
  useEffect(() => {
    const scope = pageRef.current;
    if (!scope) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ['.lib-label', '.lib-h1', '.lib-p'],
        { opacity: 0, y: 28 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.13, ease: 'power2.out', delay: 0.1 }
      );
      gsap.fromTo(
        '.lib-filters',
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out', delay: 0.45 }
      );
    }, scope);
    return () => ctx.revert();
  }, []);

  // Card entrance whenever filtered list changes
  useEffect(() => {
    if (loading || !cardsRef.current) return;
    const cards = cardsRef.current.querySelectorAll('.lib-card');
    if (!cards.length) return;
    gsap.fromTo(
      cards,
      { opacity: 0, y: 32 },
      { opacity: 1, y: 0, duration: 0.55, stagger: 0.09, ease: 'power2.out' }
    );
  }, [filtered, loading]);

  return (
    <div ref={pageRef}>
      <section className="grain relative bg-black px-6 pb-20 pt-40">
        <Monolith />
        <div className="container-x relative z-20">
          <p className="lib-label label mb-6 inline-flex items-center gap-3">
            <span className="h-px w-10 bg-gold" />
            Library
          </p>
          <h1 className="lib-h1 max-w-4xl font-display text-hero font-light text-white text-balance">
            Sample Blueprints and resources.
          </h1>
          <p className="lib-p mt-8 max-w-2xl font-sans text-base text-white/85">
            Anonymised reference documents from delivered engagements. Read
            before you book — see the document you would receive from your own
            Diagnostic.
          </p>
        </div>
      </section>

      <section className="bg-black pb-32">
        <div className="container-x">
          <div className="lib-filters mb-12">
            <LibraryFilters
              category={category}
              setCategory={setCategory}
              sector={sector}
              setSector={setSector}
              availableSectors={availableSectors}
            />
          </div>

          {loading && (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" aria-busy="true">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="grain h-64 animate-pulse rounded-4xl border border-white/10 bg-charcoal/60"
                />
              ))}
            </div>
          )}

          {!loading && (error || items.length === 0) && (
            <div className="grain mx-auto max-w-2xl rounded-5xl border border-white/10 bg-charcoal p-10 text-center">
              <p className="font-mono text-xs uppercase tracking-label text-gold">
                Library — opening soon
              </p>
              <h2 className="mt-6 font-display text-3xl font-light text-white text-balance">
                The first sample Blueprints are being curated.
              </h2>
              <p className="mt-4 font-sans text-body text-white/85">
                If you'd like a sample sent directly to your inbox in the
                meantime, book a Diagnostic or email{' '}
                <a
                  href="mailto:info@samiasystems.com"
                  className="text-gold underline decoration-gold/40 underline-offset-4 hover:decoration-gold"
                >
                  info@samiasystems.com
                </a>
                .
              </p>
              <div className="mt-8">
                <MagneticButton to="/book">Book a Diagnostic</MagneticButton>
              </div>
            </div>
          )}

          {!loading && !error && items.length > 0 && filtered.length === 0 && (
            <p className="font-sans text-body text-white/80">
              No documents match these filters yet. Try clearing one.
            </p>
          )}

          <div ref={cardsRef} className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((item) => (
              <div key={item.key} className="lib-card h-full">
                <LibraryCard item={item} onSelect={setSelected} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <DownloadFormModal item={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
